import {
  BottomSheet,
  Illustration,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { type ManagementType } from "../api/useCreatePlacementApplication";
import { type PlacementWithDetails } from "../api/useFetchPaginatedPlacements";

interface ApplyForCandidateDialogProps {
  modalState: UseModalState;
  placement: PlacementWithDetails;
  placementCandidate: PlacementCandidate;
  isLoading: boolean;
  onApply: (applicationManagementType: ManagementType) => void;
}

export function ApplyForCandidateDialog(props: ApplyForCandidateDialogProps) {
  const { modalState, placement, placementCandidate, isLoading, onApply } = props;

  const eventContext = {
    placement,
    placementCandidate,
  };

  const handleClose = () => {
    modalState.closeModal();
  };

  return (
    <BottomSheet
      modalState={modalState}
      header={<BottomSheetHeader onClose={handleClose} />}
      footer={
        <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 2 }}>
          <Stack spacing={6} width="100%">
            <LoadingButton
              variant="contained"
              size="large"
              isLoading={isLoading}
              onClick={() => {
                logEvent(APP_V2_APP_EVENTS.APPLY_FOR_CANDIDATE_DIALOG_ACTIONS, {
                  ...eventContext,
                  action: "apply-for-worker",
                });
                onApply("assisted");
              }}
            >
              Yes, apply for me
            </LoadingButton>
            {placement.webApplicationLink ? (
              <Button
                variant="outlined"
                size="large"
                disabled={isLoading}
                href={placement.webApplicationLink}
                onClick={() => {
                  logEvent(APP_V2_APP_EVENTS.APPLY_FOR_CANDIDATE_DIALOG_ACTIONS, {
                    ...eventContext,
                    action: "self-apply",
                  });
                  onApply("independent");
                }}
              >
                I&apos;ll apply myself
              </Button>
            ) : undefined}
          </Stack>
        </DialogFooter>
      }
    >
      <Box sx={{ px: 5, py: 6 }}>
        <Stack
          spacing={8}
          alignItems="center"
          justifyContent="center"
          sx={{ paddingX: 8, paddingTop: 6, paddingBottom: 10 }}
        >
          <Illustration type="shift-booked" />
          <Stack spacing={6}>
            <Text variant="h2" textAlign="center">
              Want us to handle application?
            </Text>
            <Stack spacing={2}>
              <Text>
                Because you aced your screening, you now have{" "}
                <Box component="b" sx={{ fontWeight: 800 }}>
                  access to a new perk
                </Box>
                : a dedicated recruiter! They will:
              </Text>
              <Stack component="ul" sx={{ px: 7, margin: 0 }} spacing={2}>
                <li>Apply to this job for you</li>
                <li>Reach out to workplace to speed up your application</li>
                <li>Support you directly via SMS</li>
                <li>Recommend you nearby similar jobs too</li>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </BottomSheet>
  );
}
